/**
 * Photo service - manages photo collection and business logic
 * Core domain service that UI and other adapters depend on
 */

import { Photo, PhotoId, PhotoGroup, sortPhotos, groupPhotosByMonth } from './domain.js';
import { IPhotoStorage } from './ports.js';

/**
 * Interface for photo service
 * This is what adapters (like WebUI) depend on
 */
export interface IPhotoService {
  /**
   * Load all photos, returns total count
   */
  loadPhotos(onProgress?: (count: number) => void): Promise<number>;

  /**
   * Get photos in index range with thumbnails loaded
   * Always sorted newest first (date-desc)
   */
  getPhotos(startIndex: number, endIndex: number): Promise<ReadonlyArray<Photo>>;

  /**
   * Get full-size photo URL for lightbox
   */
  getFullSizeUrl(index: number): Promise<string>;

  /**
   * Toggle star status of a photo
   */
  toggleStar(index: number): Promise<void>;

  /**
   * Set filter to show only starred photos (true) or all photos (false)
   */
  setStarFilter(showOnlyStarred: boolean): void;

  /**
   * Get current star filter state
   */
  isStarFilterActive(): boolean;

  /**
   * Get total count of photos (respects current filter)
   */
  getTotalCount(): number;

  /**
   * Get EXIF extraction progress
   * Returns null if no extraction is in progress
   */
  getExifExtractionProgress(): { completed: number; total: number; isRunning: boolean } | null;

  /**
   * Check if a reload is needed due to a write conflict
   */
  needsReloadFromConflict(): boolean;

  /**
   * Get photo groups (by month and year)
   * Returns groups in same order as photos (newest first)
   */
  getPhotoGroups(): PhotoGroup[];

  /**
   * Get photos by their IDs and trigger thumbnail loading
   * Useful for loading thumbnails for specific photos (e.g., cart items)
   */
  getPhotosByIds(ids: ReadonlySet<PhotoId>): Promise<ReadonlyArray<Photo>>;
}

/**
 * Implementation of photo service
 */
export class PhotoService implements IPhotoService {
  private allPhotos: Photo[] = [];
  private photos: Photo[] = [];
  private storage: IPhotoStorage;
  private showOnlyStarred: boolean = false;

  // Debouncing state for thumbnail loading
  private pendingThumbnailPhotos: Photo[] = [];
  private thumbnailDebounceTimer?: ReturnType<typeof setTimeout>;
  private isLoadingThumbnails = false;
  private readonly thumbnailDebounceMs = 150; // Debounce thumbnail requests by 150ms

  constructor(storage: IPhotoStorage) {
    this.storage = storage;
  }

  async loadPhotos(onProgress?: (count: number) => void): Promise<number> {
    const photos = await this.storage.scanPhotos(onProgress);

    // Sort once - newest first
    this.allPhotos = [...sortPhotos(photos)];
    this.applyFilter();

    return this.photos.length;
  }

  private applyFilter(): void {
    if (this.showOnlyStarred) {
      this.photos = this.allPhotos.filter(p => p.isStarred);
    } else {
      this.photos = this.allPhotos;
    }
  }

  async getPhotos(startIndex: number, endIndex: number): Promise<ReadonlyArray<Photo>> {
    // Get the requested range (already sorted)
    const requestedPhotos = this.photos.slice(startIndex, endIndex);

    // Find photos that need thumbnails
    const photosNeedingThumbnails = requestedPhotos.filter((p) => !p.thumbnailUrl);

    if (photosNeedingThumbnails.length > 0) {
      // Kick off thumbnail loading in background (don't await)
      this.loadThumbnailsInBackground(photosNeedingThumbnails);
    }

    // Return immediately with whatever we have (placeholders will show for missing thumbnails)
    return requestedPhotos;
  }

  /**
   * Load thumbnails in background and update cached photos
   * Uses debouncing - replaces pending queue with latest request to avoid loading stale positions
   */
  private loadThumbnailsInBackground(photos: Photo[]): void {
    // Replace pending queue entirely - we only care about the most recent scroll position
    // This discards thumbnails from intermediate scroll positions the user scrolled past
    this.pendingThumbnailPhotos = photos.filter(p => !p.thumbnailUrl);

    // Clear existing debounce timer
    if (this.thumbnailDebounceTimer) {
      clearTimeout(this.thumbnailDebounceTimer);
    }

    // Set new debounce timer
    this.thumbnailDebounceTimer = setTimeout(() => {
      this.flushThumbnailQueue();
    }, this.thumbnailDebounceMs);
  }

  /**
   * Flush the pending thumbnail queue and make the API request
   */
  private async flushThumbnailQueue(): Promise<void> {
    // Don't start a new request if one is already in progress
    if (this.isLoadingThumbnails || this.pendingThumbnailPhotos.length === 0) {
      return;
    }

    this.isLoadingThumbnails = true;

    // Take the current batch and clear the queue
    const photosToLoad = [...this.pendingThumbnailPhotos];
    this.pendingThumbnailPhotos = [];

    try {
      const thumbnailMap = await this.storage.fetchThumbnails(photosToLoad);

      // Update cached photos with thumbnails (in allPhotos)
      thumbnailMap.forEach((thumbnailUrl, photoId) => {
        const photo = this.allPhotos.find((p) => p.id === photoId);
        if (photo) {
          const index = this.allPhotos.indexOf(photo);
          this.allPhotos[index] = { ...photo, thumbnailUrl };
        }
      });

      // Reapply filter to update photos array
      this.applyFilter();
    } catch (error) {
      console.error('Error loading thumbnails in background:', error);
    } finally {
      this.isLoadingThumbnails = false;

      // If more photos were queued while we were loading, process them
      if (this.pendingThumbnailPhotos.length > 0) {
        this.thumbnailDebounceTimer = setTimeout(() => {
          this.flushThumbnailQueue();
        }, this.thumbnailDebounceMs);
      }
    }
  }

  async getFullSizeUrl(index: number): Promise<string> {
    const photo = this.photos[index];
    if (!photo) {
      throw new Error(`Photo at index ${index} not found`);
    }
    return await this.storage.getFullSizeUrl(photo);
  }

  async toggleStar(index: number): Promise<void> {
    const photo = this.photos[index];
    if (!photo) {
      throw new Error(`Photo at index ${index} not found`);
    }

    // Toggle in storage
    if (photo.isStarred) {
      await this.storage.unstarPhoto(photo);
    } else {
      await this.storage.starPhoto(photo);
    }

    // Update in allPhotos cache
    const allPhotoIndex = this.allPhotos.findIndex(p => p.id === photo.id);
    if (allPhotoIndex !== -1) {
      this.allPhotos[allPhotoIndex] = { ...photo, isStarred: !photo.isStarred };
    }

    // Reapply filter to update photos array
    this.applyFilter();
  }

  setStarFilter(showOnlyStarred: boolean): void {
    this.showOnlyStarred = showOnlyStarred;
    this.applyFilter();
  }

  isStarFilterActive(): boolean {
    return this.showOnlyStarred;
  }

  getTotalCount(): number {
    return this.photos.length;
  }

  getExifExtractionProgress(): { completed: number; total: number; isRunning: boolean } | null {
    return this.storage.getExifExtractionProgress();
  }

  needsReloadFromConflict(): boolean {
    return this.storage.needsReloadFromConflict();
  }

  getPhotoGroups(): PhotoGroup[] {
    // Group the currently filtered photos
    return groupPhotosByMonth(this.photos);
  }

  async getPhotosByIds(ids: ReadonlySet<PhotoId>): Promise<ReadonlyArray<Photo>> {
    // Find photos matching the IDs (search in allPhotos to include filtered-out photos)
    const matchingPhotos = this.allPhotos.filter(p => ids.has(p.id));

    // Trigger thumbnail loading for photos that need it
    const photosNeedingThumbnails = matchingPhotos.filter(p => !p.thumbnailUrl);
    if (photosNeedingThumbnails.length > 0) {
      this.loadThumbnailsInBackground(photosNeedingThumbnails);
    }

    return matchingPhotos;
  }
}
